import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import * as Sentry from "@sentry/react";
import { isSentryEnabled } from "./sentry";
import { regionPinnedFetch } from "./functionRegion";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// ─── Token exchange cache ────────────────────────────────
interface CachedToken {
  accessToken: string;
  expiresAt: number; // Unix timestamp in ms
}

let cachedToken: CachedToken | null = null;
let inflightExchange: Promise<string | null> | null = null;
let privyTokenGetter: (() => Promise<string | null>) | null = null;

// Negative cache for failed exchanges. Without it, waitForSupabaseAccessToken's
// 100ms poll loop issues a fresh POST to the token-exchange EF on every tick
// during an EF outage (failures are not cached and inflightExchange clears in
// `finally`), and with assertAdmin() wired into every admin adapter one open
// admin tab can amplify an outage into hundreds of requests per poll cycle.
// 5s > the 3s wait window, so a failing exchange costs at most one POST per
// guard call. A successful exchange, logout, or fresh login getter resets it.
const EXCHANGE_FAILURE_BACKOFF_MS = 5_000;
let exchangeBackoffUntil = 0;

/**
 * Called once from usePrivySync to provide the Privy getAccessToken function.
 * This avoids a circular dependency between Supabase client and Privy hooks.
 */
export function setPrivyTokenGetter(
  getter: () => Promise<string | null>,
): void {
  privyTokenGetter = getter;
  // New auth context — a backoff from the previous session must not delay it.
  exchangeBackoffUntil = 0;
}

/**
 * Exchange a Privy JWT for a Supabase-compatible JWT via the token-exchange EF.
 * Caches the result and deduplicates concurrent requests.
 *
 * Exported so callers that hit edge functions directly (not via the
 * supabase-js client) can attach the same Authorization header — e.g.,
 * llm-proxy. Returns null when Privy hasn't hydrated yet or the
 * exchange failed; callers should treat null as "not authenticated."
 */
export async function getSupabaseAccessToken(): Promise<string | null> {
  if (!privyTokenGetter) return null;

  // Return cached token if still valid (with 60s buffer)
  if (cachedToken && cachedToken.expiresAt > Date.now() + 60_000) {
    return cachedToken.accessToken;
  }

  // Deduplicate concurrent requests
  if (inflightExchange) return inflightExchange;

  // Recent exchange failure — don't hammer the EF; callers treat null as
  // "not authenticated" exactly as they would for a live failure.
  if (Date.now() < exchangeBackoffUntil) return null;

  const exchange = (async (): Promise<string | null> => {
    try {
      const privyJwt = await privyTokenGetter!();
      if (!privyJwt) return null;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/token-exchange`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${privyJwt}`,
            "Content-Type": "application/json",
            apikey: supabaseAnonKey,
          },
        },
      );

      if (!response.ok) {
        const body = await response.text();
        console.error("[supabase] Token exchange failed:", response.status, body);
        exchangeBackoffUntil = Date.now() + EXCHANGE_FAILURE_BACKOFF_MS;
        return null;
      }

      const data = await response.json();
      const token = data.access_token as string;

      // Cache with expiry
      cachedToken = {
        accessToken: token,
        expiresAt: Date.now() + (data.expires_in as number) * 1000,
      };
      exchangeBackoffUntil = 0;

      return token;
    } catch (err) {
      console.error("[supabase] Token exchange error:", err);
      exchangeBackoffUntil = Date.now() + EXCHANGE_FAILURE_BACKOFF_MS;
      return null;
    } finally {
      inflightExchange = null;
    }
  })();

  inflightExchange = exchange;
  return exchange;
}

/**
 * Like getSupabaseAccessToken, but waits up to `timeoutMs` for Privy
 * to hydrate before giving up. Use this in callers that fire shortly
 * after page load (e.g. llm-proxy callers), where the user is logged
 * in but `setPrivyTokenGetter` or the first token fetch hasn't
 * completed yet.
 */
export async function waitForSupabaseAccessToken(
  timeoutMs = 3000,
): Promise<string | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const token = await getSupabaseAccessToken();
    if (token) return token;
    await new Promise((r) => setTimeout(r, 100));
  }
  return null;
}

/**
 * Invalidate the cached token so the next `getSupabaseAccessToken` call
 * forces a fresh exchange. Unlike `clearTokenCache` this preserves the
 * Privy getter so the user stays logged in.
 */
export function invalidateCachedToken(): void {
  cachedToken = null;
}

/**
 * Clear the cached Supabase token (e.g. on logout).
 */
export function clearTokenCache(): void {
  cachedToken = null;
  inflightExchange = null;
  privyTokenGetter = null;
  exchangeBackoffUntil = 0;
}

// ─── Retry-on-401 fetch wrapper ──────────────────────────
// PostgREST returns 401 "JWT expired" (PGRST301) when the token expires
// between the client-side cache check and the server. This wrapper
// detects that, invalidates the cached token, fetches a fresh one, and
// retries the request once — making the expiry invisible to the caller.
export const jwtRetryFetch: typeof fetch = async (input, init) => {
  const res = await regionPinnedFetch(input, init);
  if (res.status !== 401) return res;

  // Only retry PostgREST/REST-style 401s (not edge-function auth errors
  // which use a different flow). Read the body to check, but clone first
  // so the original response is still consumable if we don't retry.
  const cloned = res.clone();
  let body: { message?: string; code?: string } | null = null;
  try {
    body = await cloned.json();
  } catch {
    return res;
  }
  if (body?.message !== "JWT expired") return res;

  // Force a fresh exchange and retry once.
  invalidateCachedToken();
  const freshToken = await getSupabaseAccessToken();
  if (!freshToken) return res;

  const retryInit = { ...init, headers: new Headers(init?.headers) };
  retryInit.headers.set("Authorization", `Bearer ${freshToken}`);

  // Normalise `input` to a string URL so the region-pin logic runs again.
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  return regionPinnedFetch(url, retryInit);
};

// ─── Supabase client ─────────────────────────────────────
// NOTE: Supabase Realtime is intentionally unused. The Privy token-exchange
// auth setup causes WebSocket connections to fail with infinite reconnect loops.
// All features use polling (refetchInterval / setInterval) instead.
// See useWorkspaceChat.ts for the original migration.
// TODO: Revisit Realtime once WebSocket auth is compatible with Privy.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  accessToken: getSupabaseAccessToken,
  // Edge functions run beside the database, not beside the caller.
  // jwtRetryFetch composes regionPinnedFetch with a single-retry on 401.
  global: { fetch: jwtRetryFetch },
});

// ─── Sentry Supabase integration ────────────────────────
if (isSentryEnabled) {
  Sentry.addIntegration(
    supabaseIntegration(SupabaseClient, Sentry, {
      tracing: true,
      breadcrumbs: true,
      errors: true,
    }),
  );
}
